// components/location/BusinessLocationRow.tsx

import { router } from "expo-router";
import { MapPin } from "lucide-react-native";
import React from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

type Props = {
  businessName?: string | null;
  lat?: number | null;
  lng?: number | null;
  mapTitle?: string | null;
  mapSubtitle?: string | null;
  addressLine?: string | null;

  /**
   * Visible line 1 on the card.
   * Examples:
   * - "Online Shop"
   * - "Freelance"
   * - "D12, 1st Floor, DK House"
   * - "Upper Hill, Nairobi"
   */
  line1: string;

  /**
   * Visible line 2 on the card.
   * Example:
   * - "Upper Hill, Nairobi"
   */
  line2?: string | null;

  /**
   * Controls whether the row should open the map.
   *
   * Use false for:
   * - Online Shop
   * - Freelance
   *
   * Use true only when the business has a real physical pin/location.
   */
  canOpenMap?: boolean;
};

function cleanText(value?: string | null) {
  return String(value ?? "").trim();
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export default function BusinessLocationRow({
  businessName,
  lat,
  lng,
  mapTitle,
  mapSubtitle,
  addressLine,
  line1,
  line2,
  canOpenMap,
}: Props) {
  const hasCoordinates = isFiniteNumber(lat) && isFiniteNumber(lng);

  const visibleLine1 =
    cleanText(line1) ||
    cleanText(addressLine) ||
    cleanText(mapTitle) ||
    "Business location";

  const visibleLine2 = cleanText(line2) || cleanText(mapSubtitle);
  const address = cleanText(addressLine);

  const title =
    cleanText(businessName) || cleanText(mapTitle) || visibleLine1 || "Location";

  const subtitle = visibleLine2 || cleanText(mapSubtitle);

  const fallbackQuery = [address, visibleLine2, visibleLine1]
    .filter(Boolean)
    .join(", ");

  const allowMapOpen = canOpenMap !== false;

  const shouldOpenMap =
    allowMapOpen && (hasCoordinates || fallbackQuery.length > 0);

  function handleOpenMap() {
    if (!shouldOpenMap) return;

    router.push({
      pathname: "/location-map",
      params: {
        lat: hasCoordinates ? String(lat) : "",
        lng: hasCoordinates ? String(lng) : "",

        businessName: title,
        title,

        subtitle,
        addressLine: address,
        cityLine: visibleLine2,

        query: fallbackQuery,
      },
    });
  }

  return (
    <Pressable
      style={({ pressed }) => [
        styles.row,
        pressed && shouldOpenMap ? styles.pressed : null,
      ]}
      onPress={handleOpenMap}
      disabled={!shouldOpenMap}
    >
      <View style={styles.iconWrap}>
        <MapPin size={20} color="#ffffff" strokeWidth={2.5} />
      </View>

      <View style={styles.textWrap}>
        <Text style={styles.line1} numberOfLines={2} ellipsizeMode="tail">
          {visibleLine1}
        </Text>

        {!!visibleLine2 && (
          <Text style={styles.line2} numberOfLines={1} ellipsizeMode="tail">
            {visibleLine2}
          </Text>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
  },

  pressed: {
    opacity: 0.75,
  },

  iconWrap: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: "#0f172a",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 2,
  },

  textWrap: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },

  line1: {
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "800",
    color: "#0f172a",
  },

  line2: {
    marginTop: 2,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "500",
    color: "#64748b",
  },
});
